import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DateTimePicker } from '@nucleus-commerce/admin-kit/date-time';
import { SummaryCard } from '@nucleus-commerce/admin-kit/forms';
import type {
  ProductSummaryStatus,
  StoreProductSchedule,
} from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { fromZonedInput } from '@nucleus-commerce/core';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { ToggleGroup, ToggleGroupItem } from '@nucleus-commerce/ui/components/toggle-group';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { storeProductLiveState, type StoreProductLiveState } from '../schemas/live-state';
import { publishConflict, STORE_PRODUCT_STATUSES } from '../schemas/publication-rules';
import type { StoreProductFormInput } from '../schemas/store-product-form-schema';

import { PublishConflictAlert } from './publish-conflict-alert';

const DOT: Record<StoreProductLiveState['kind'], string> = {
  live: 'bg-success',
  scheduled: 'bg-primary',
  hidden: 'bg-warning',
  draft: 'bg-muted-foreground',
  unpublished: 'bg-muted-foreground',
};

export interface StoreStatusCardProps {
  productId: string;
  masterStatus: ProductSummaryStatus;
  /** The last failed save, for the refusal to publish a master product that isn't active. */
  saveError: unknown;
  /**
   * The rotation that owns this store product, if any: its runs set the status, so the one-shot
   * publish and unpublish dates are off (the backend refuses them with SCHEDULE_CONFLICT).
   */
  owner: StoreProductSchedule | null;
}

/**
 * This store's status and schedule. The line on top says what shoppers get with what's on the
 * page; a refused publish is explained here with a link to the master. The save bar saves it.
 */
export function StoreStatusCard({
  productId,
  masterStatus,
  saveError,
  owner,
}: StoreStatusCardProps) {
  const { t } = useTranslation('storeCatalog');
  const format = useFormat();
  const form = useFormContext<StoreProductFormInput>();
  const [status, publishAt] = useWatch({ control: form.control, name: ['status', 'publishAt'] });
  const state = storeProductLiveState({
    status,
    masterStatus,
    publishAt: fromZonedInput(publishAt, format.timeZone),
  });
  const conflict = publishConflict(saveError);

  return (
    <SummaryCard title={t('sections.status')} tour="store-catalog.product.status-save">
      <p className="flex items-baseline gap-1.5 text-[13px]">
        <span
          className={cn('size-2 shrink-0 self-center rounded-full', DOT[state.kind])}
          aria-hidden
        />
        <span>
          <b className="font-semibold">
            {state.kind === 'hidden'
              ? t(`storePage.live.hidden.${state.master}`)
              : t(`storePage.statusLine.${state.kind}.label`)}
          </b>
          <span className="text-muted-foreground">
            {' · '}
            {state.kind === 'scheduled'
              ? t('storePage.statusLine.scheduled.detail', { when: format.dateTime(state.at) })
              : t(`storePage.statusLine.${state.kind}.detail`)}
          </span>
        </span>
      </p>
      <FormField
        control={form.control}
        name="status"
        render={({ field }) => (
          <FormItem className="gap-2">
            <FormControl>
              <ToggleGroup
                type="single"
                disabled={form.formState.disabled}
                role="radiogroup"
                spacing={1}
                aria-label={t('storePage.statusLine.group')}
                value={field.value}
                onValueChange={(next) => {
                  // A segmented control always holds a value: clicking the current one is a no-op.
                  if (next) field.onChange(next);
                }}
                className="bg-muted grid w-full grid-cols-3 gap-0.5 rounded-lg p-0.5"
              >
                {STORE_PRODUCT_STATUSES.map((value) => (
                  <ToggleGroupItem
                    key={value}
                    value={value}
                    className="text-muted-foreground data-[state=on]:bg-background data-[state=on]:text-foreground hover:text-foreground h-7 w-full rounded-md px-1 text-xs font-normal hover:bg-transparent data-[state=on]:font-semibold data-[state=on]:shadow-xs"
                  >
                    {t(`status.${value}`)}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <span className="text-muted-foreground text-xs">{t(`storePage.statusHint.${status}`)}</span>
      {conflict ? (
        <PublishConflictAlert
          productId={productId}
          productStatus={conflict.productStatus}
          requestId={isApiError(saveError) ? saveError.requestId : undefined}
        />
      ) : masterStatus !== 'active' ? (
        <p className="text-muted-foreground text-xs">
          {t('publishConflict.masterNotActive', { status: t(`masterStatus.${masterStatus}`) })}
        </p>
      ) : null}
      <div
        className="mt-1 flex flex-col gap-3 border-t pt-3"
        data-tour="store-catalog.product.schedule"
      >
        {owner ? (
          <p id="store-schedule-owned" className="text-muted-foreground text-xs">
            {t('owner.datesOff', { name: owner.name })}
          </p>
        ) : null}
        {(['publishAt', 'unpublishAt'] as const).map((name) => (
          <FormField
            key={name}
            control={form.control}
            name={name}
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between gap-2">
                  <FormLabel>{t(`storePage.schedule.${name}`)}</FormLabel>
                  {field.value && !owner && !form.formState.disabled ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      className="text-muted-foreground -my-1 h-6"
                      onClick={() => field.onChange('')}
                      aria-label={t(`fields.${name}Clear`)}
                    >
                      {t('fields.clearSchedule')}
                    </Button>
                  ) : null}
                </div>
                <FormControl>
                  <DateTimePicker
                    {...field}
                    timeZone={format.timeZone}
                    disabled={owner !== null || form.formState.disabled}
                    {...(owner ? { 'aria-describedby': 'store-schedule-owned' } : {})}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ))}
        <span className="text-muted-foreground text-xs">
          {t('storePage.schedule.clock', { zone: format.timeZone })}
        </span>
      </div>
    </SummaryCard>
  );
}
